/**
 * Heightfield container format (SPEC §3.9.1).
 *
 * The theatre is generated at build time and committed, so this codec is shared by the generator
 * script and the runtime loader — one implementation, no chance of the writer and the reader
 * drifting apart.
 *
 * Getting a 2048² heightfield under the 1.5 MB budget takes three things, and all three are
 * needed; a naive 16-bit dump gzips to over 4 MB:
 *
 *  1. **A quantisation step chosen for terrain, not for the format.** Spending all 16 bits on a
 *     1500 m range gives 2 cm of vertical precision — far finer than the half-float texture the
 *     GPU renders from, and every one of those wasted bits is noise the compressor must store.
 *  2. **A gradient predictor** (the MED predictor from LOCO-I/JPEG-LS). Predicting from the left
 *     neighbour alone leaves the whole slope in the residual; predicting from left, above and
 *     above-left removes the planar part and leaves only curvature.
 *  3. **Byte-plane splitting.** Residuals are small but stored in two bytes, so interleaved the
 *     high byte — almost always 0x00 or 0xFF — is scattered through the stream where deflate's
 *     window cannot exploit it. Split into two planes it collapses to nearly nothing.
 *
 * Little-endian throughout. Units: metres.
 */

/** The bytes 'V','Z','T','2' in file order, read as a little-endian uint32. */
export const TERRAIN_MAGIC = 0x32545a56;
export const TERRAIN_HEADER_BYTES = 24;

export interface TerrainHeader {
  /** Samples per side; the grid is square. */
  resolution: number;
  /** World extent per side, metres. */
  worldSizeM: number;
  /** Height mapped to quantised 0. */
  minHeightM: number;
  /** Metres per quantisation step. */
  heightStepM: number;
}

export interface DecodedTerrain {
  header: TerrainHeader;
  /** Heights in metres, row-major, `resolution²` entries. */
  heights: Float32Array;
}

function zigzag(value: number): number {
  return ((value << 1) ^ (value >> 31)) & 0xffff;
}

function unzigzag(value: number): number {
  return (value >>> 1) ^ -(value & 1);
}

/**
 * The MED predictor: `left + above − aboveLeft`, clamped to the bracket the neighbours imply.
 * The clamp is what makes it robust at edges — it degrades to a plain neighbour copy across a
 * cliff instead of extrapolating off it.
 */
function predict(left: number, above: number, aboveLeft: number): number {
  const max = left > above ? left : above;
  const min = left > above ? above : left;
  if (aboveLeft >= max) return min;
  if (aboveLeft <= min) return max;
  return left + above - aboveLeft;
}

/** Encode a heightfield to the uncompressed payload. Gzip is applied by the caller. */
export function encodeTerrain(header: TerrainHeader, heights: Float32Array): Uint8Array {
  const { resolution, minHeightM, heightStepM } = header;
  const sampleCount = resolution * resolution;
  if (heights.length !== sampleCount) {
    throw new Error(`encodeTerrain: expected ${sampleCount} samples, received ${heights.length}`);
  }
  if (!(heightStepM > 0)) throw new Error('encodeTerrain: heightStepM must be positive');

  // Out-of-range heights are an error, not something to clamp away. A silently flattened peak
  // would show up much later as terrain that does not match its own collision.
  const quantised = new Uint16Array(sampleCount);
  for (let i = 0; i < sampleCount; i += 1) {
    const level = Math.round((heights[i] - minHeightM) / heightStepM);
    if (level < 0 || level > 65535) {
      throw new Error(
        `encodeTerrain: height ${heights[i].toFixed(2)} m is outside the representable range ` +
          `${minHeightM} … ${(minHeightM + 65535 * heightStepM).toFixed(2)} m`,
      );
    }
    quantised[i] = level;
  }

  const output = new Uint8Array(TERRAIN_HEADER_BYTES + sampleCount * 2);
  const view = new DataView(output.buffer);
  view.setUint32(0, TERRAIN_MAGIC, true);
  view.setUint32(4, resolution, true);
  view.setFloat32(8, header.worldSizeM, true);
  view.setFloat32(12, minHeightM, true);
  view.setFloat32(16, heightStepM, true);
  view.setUint32(20, 0, true); // reserved

  // Two planes: all low bytes, then all high bytes.
  const lowPlane = TERRAIN_HEADER_BYTES;
  const highPlane = TERRAIN_HEADER_BYTES + sampleCount;

  for (let row = 0; row < resolution; row += 1) {
    const rowStart = row * resolution;
    for (let column = 0; column < resolution; column += 1) {
      const index = rowStart + column;
      const left = column === 0 ? 0 : quantised[index - 1];
      const above = row === 0 ? 0 : quantised[index - resolution];
      const aboveLeft = row === 0 || column === 0 ? 0 : quantised[index - resolution - 1];
      const residual = ((quantised[index] - predict(left, above, aboveLeft)) << 16) >> 16;
      const encoded = zigzag(residual);
      output[lowPlane + index] = encoded & 0xff;
      output[highPlane + index] = (encoded >>> 8) & 0xff;
    }
  }

  return output;
}

/** Decode an uncompressed payload produced by encodeTerrain. */
export function decodeTerrain(payload: Uint8Array): DecodedTerrain {
  if (payload.byteLength < TERRAIN_HEADER_BYTES) {
    throw new Error('decodeTerrain: payload is shorter than the header');
  }
  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  const magic = view.getUint32(0, true);
  if (magic !== TERRAIN_MAGIC) {
    throw new Error(`decodeTerrain: bad magic 0x${magic.toString(16)}`);
  }

  const resolution = view.getUint32(4, true);
  const header: TerrainHeader = {
    resolution,
    worldSizeM: view.getFloat32(8, true),
    minHeightM: view.getFloat32(12, true),
    heightStepM: view.getFloat32(16, true),
  };

  const sampleCount = resolution * resolution;
  const expectedBytes = TERRAIN_HEADER_BYTES + sampleCount * 2;
  if (payload.byteLength < expectedBytes) {
    throw new Error(
      `decodeTerrain: expected ${expectedBytes} bytes for ${resolution}², received ${payload.byteLength}`,
    );
  }

  const lowPlane = TERRAIN_HEADER_BYTES;
  const highPlane = TERRAIN_HEADER_BYTES + sampleCount;

  const quantised = new Uint16Array(sampleCount);
  for (let row = 0; row < resolution; row += 1) {
    const rowStart = row * resolution;
    for (let column = 0; column < resolution; column += 1) {
      const index = rowStart + column;
      const left = column === 0 ? 0 : quantised[index - 1];
      const above = row === 0 ? 0 : quantised[index - resolution];
      const aboveLeft = row === 0 || column === 0 ? 0 : quantised[index - resolution - 1];
      const encoded = payload[lowPlane + index] | (payload[highPlane + index] << 8);
      quantised[index] = (predict(left, above, aboveLeft) + unzigzag(encoded)) & 0xffff;
    }
  }

  const heights = new Float32Array(sampleCount);
  for (let i = 0; i < sampleCount; i += 1) {
    heights[i] = header.minHeightM + quantised[i] * header.heightStepM;
  }

  return { header, heights };
}
